import { forwardRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  DataTable,
  PageHeader,
  Input,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Label,
  SpinnerLoader,
  Toastify,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import { Edit, RefreshCw, Search } from 'lucide-react';
import { debounce } from '@lift-admin/utils/debounce';
import { useThrottledFn } from '@lift-admin/utils/throttle';
import apiClient from '@lift-admin/utils/apiClient';
import { buildSummary, diffFields } from '@lift-admin/utils/logChangeFormatter';
import { actionLog } from '../../App';
import { useAuthContext } from '@lift-admin/utils/provider';

const MatrixComponent = forwardRef(() => {
  const user = JSON.parse(localStorage.getItem('user') as string);
  const { canEdit } = useAuthContext();
  const hasEditAccess = canEdit('affiliate_mgmt');
  const admin_data = localStorage.getItem('admin_auth')
    ? JSON.parse(localStorage.getItem('admin_auth') || '')
    : null;

  const [brandPubList, setBrandPubList] = useState<any[]>([]);
  const [brandPubListBackup, setBrandPubListBackup] = useState<any[]>([]);
  const [searchText, setSearchText] = useState('');
  const [listLoad, setListLoad] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [showEditBrandWeightDialog, setShowEditBrandWeightDialog] = useState(false);
  const [hideEditBrandWeightProgressDialog, setHideEditBrandWeightProgressDialog] = useState(true);

  const [brandGoals, setBrandGoals] = useState({
    id: -1,
    revenue: 0,
    spend: 0,
    comm_rate: 0,
    conv_rate: 0,
    clicks: 0,
    actions: 0,
    cac: 0,
    new_user: 0,
    new_rev: 0,
    discount: 0,
    aov: 0,
    roas: 0,
    canc: 0,
  });

  const getBrandGoals = async () => {
    try {
      setListLoad(true);
      const res = await apiClient.get(`/brand/v1/admin/brand/goals/get`);

      const sortedData = res.data.response.sort((a: any, b: any) => {
        return (
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      });

      setBrandPubList(sortedData);
      setBrandPubListBackup(sortedData);
      setPageIndex(0);
    } catch (error: any) {
      setListLoad(false);
      Toastify('error', error.response?.data?.message || 'Failed to fetch brand goals');
      console.log(error);
    } finally {
      setListLoad(false);
    }
  };

  const throttleGetBrandGoals = useThrottledFn(getBrandGoals, 1000);

  // Search results
  const searchResults = (query: any) => {
    let results: any = [];
    for (let i = 0; i < brandPubListBackup.length; i++) {
      if (
        brandPubListBackup[i]['brand']?.name
          ?.toLowerCase()
          .includes(query.toLowerCase())
      )
        results.push(brandPubListBackup[i]);
    }
    setBrandPubList(results);
  };

  const searchOptimized = useCallback(debounce(searchResults, 600), [
    brandPubListBackup,
  ]);

  useEffect(() => {
    getBrandGoals();
  }, []);

  const editBrandWeight = (row: any) => {
    setBrandGoals({ ...row });
    setShowEditBrandWeightDialog(true);
  };

  const handleChange = (e: any) => {
    setBrandGoals({
      ...brandGoals,
      [e.target.name]: parseInt(e.target.value) || 0,
    });
  };

  const changeDetected = () => {
    const brand = brandPubListBackup.find((b: any) => b.id === brandGoals.id);
    const changes = [];

    if (brand) {
      if (brand.actions != brandGoals.actions) {
        changes.push(`${brand.actions} => ${brandGoals.actions} (actions)`);
      }
      if (brand.aov != brandGoals.aov) {
        changes.push(`${brand.aov} => ${brandGoals.aov} (aov)`);
      }
      if (brand.cac != brandGoals.cac) {
        changes.push(`${brand.cac} => ${brandGoals.cac} (cac)`);
      }
      if (brand.canc != brandGoals?.canc) {
        changes.push(`${brand.canc} => ${brandGoals?.canc} (canc)`);
      }
      if (brand.clicks != brandGoals.clicks) {
        changes.push(`${brand.clicks} => ${brandGoals.clicks} (clicks)`);
      }
      if (brand.comm_rate != brandGoals.comm_rate) {
        changes.push(
          `${brand.comm_rate} => ${brandGoals.comm_rate} (comm_rate)`
        );
      }
      if (brand.conv_rate != brandGoals.conv_rate) {
        changes.push(
          `${brand.conv_rate} => ${brandGoals.conv_rate} (conv_rate)`
        );
      }
      if (brand.discount != brandGoals.discount) {
        changes.push(`${brand.discount} => ${brandGoals.discount} (discount)`);
      }
      if (brand.new_rev != brandGoals.new_rev) {
        changes.push(`${brand.new_rev} => ${brandGoals.new_rev} (new_rev)`);
      }
      if (brand.new_user != brandGoals.new_user) {
        changes.push(`${brand.new_user} => ${brandGoals.new_user} (new_user)`);
      }
      if (brand.revenue != brandGoals.revenue) {
        changes.push(`${brand.revenue} => ${brandGoals.revenue} (revenue)`);
      }
      if (brand.roas != brandGoals.roas) {
        changes.push(`${brand.roas} => ${brandGoals.roas} (roas)`);
      }
      if (brand.spend != brandGoals.spend) {
        changes.push(`${brand.spend} => ${brandGoals.spend} (spend)`);
      }
    }

    return changes;
  };

  const updateBrandGoal = async () => {
    try {
      setHideEditBrandWeightProgressDialog(false);
      const res = await apiClient.put(`/brand/v1/admin/brand/goals/update`, {
        ...brandGoals,
      });

      setShowEditBrandWeightDialog(false);
      setHideEditBrandWeightProgressDialog(true);
      getBrandGoals();
      Toastify('success', 'Brand goal updated!');
      const oldGoal = brandPubListBackup.find((b: any) => b.id === brandGoals.id) || {};
      const goalBrandName =
        oldGoal.brand?.name ||
        (brandGoals as any).brand?.name ||
        oldGoal.name ||
        (brandGoals as any).name;
      const goalChanges = diffFields(oldGoal, brandGoals, [
        { key: 'actions', label: 'Actions' },
        { key: 'aov', label: 'AOV' },
        { key: 'cac', label: 'CAC' },
        { key: 'canc', label: 'Cancellations' },
        { key: 'clicks', label: 'Clicks' },
        { key: 'comm_rate', label: 'Commission Rate' },
        { key: 'conv_rate', label: 'Conversion Rate' },
        { key: 'discount', label: 'Discount' },
        { key: 'new_rev', label: 'New Revenue' },
        { key: 'new_user', label: 'New Users' },
        { key: 'revenue', label: 'Revenue' },
        { key: 'roas', label: 'ROAS' },
        { key: 'spend', label: 'Spend' },
      ]);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const desc = {
        message: buildSummary('Brand goals', goalBrandName, 'updated', goalChanges.length),
        category: 'brand',
        status: res.status,
        method: res.config.method,
        url: res.config.url,
        user_id: user?.id,
        user_name: user?.name,
        brand_name: goalBrandName,
        changes: goalChanges,
      };

      await actionLog?.actionLogTrack(JSON.stringify(desc), false);
    } catch (error: any) {
      console.log(error, 'error');
      setHideEditBrandWeightProgressDialog(true);
      if (error.response) {
        Toastify('error', error.response.data.message);
      } else {
        Toastify('error', 'Something went wrong!');
      }

      const failedBrandName =
        (brandGoals as any).brand?.name ||
        brandPubListBackup.find((b: any) => b.id === brandGoals.id)?.brand?.name;
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const errorDesc = {
        message: failedBrandName
          ? `Failed to update brand goals "${failedBrandName}": ${error.response?.data?.message || error.message}`
          : `Failed to update brand goals: ${error.response?.data?.message || error.message}`,
        category: 'brand',
        status: error.response?.status,
        method: 'PUT',
        url: `/brand/v1/admin/brand/goals/update`,
        user_id: user?.id,
        user_name: user?.name,
        brand_name: failedBrandName,
        error: error.response?.data?.message || error.message,
      };
      await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
    }
  };

  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return brandPubList.slice(start, start + pageSize);
  }, [brandPubList, pageIndex, pageSize]);

  const dataTableColumns = useMemo(() => {
    const cols: TableColumn<any>[] = [
      {
        key: 'brand',
        header: 'Brand Name',
        accessor: (row: any) => row.brand?.name,
        render: (_v: any, row: any) => (
          <div className="font-medium">{row.brand?.name}</div>
        ),
      },
      { key: 'revenue', header: 'Revenue', accessor: 'revenue' },
      { key: 'spend', header: 'Spend', accessor: 'spend' },
      { key: 'comm_rate', header: 'Comm Rate', accessor: 'comm_rate' },
      { key: 'conv_rate', header: 'Conv Rate', accessor: 'conv_rate' },
      { key: 'clicks', header: 'Clicks', accessor: 'clicks' },
      { key: 'actions', header: 'New Actions', accessor: 'actions' },
      { key: 'cac', header: 'CAC', accessor: 'cac' },
      {
        key: 'canc',
        header: 'CAnC',
        accessor: 'canc',
        render: (_v: any, row: any) => (row.canc != null && row.canc !== '' ? row.canc : '--'),
      },
      { key: 'new_user', header: 'New Users %', accessor: 'new_user' },
      { key: 'new_rev', header: 'New Rev %', accessor: 'new_rev' },
      { key: 'discount', header: 'Discount %', accessor: 'discount' },
      { key: 'aov', header: 'AOV', accessor: 'aov' },
      { key: 'roas', header: 'ROAS', accessor: 'roas' },
    ];
    if (hasEditAccess) {
      cols.push({
        key: 'matrix_setup',
        header: 'Matrix Setup',
        accessor: (row: any) => row.id,
        width: '100px',
        align: 'center' as const,
        render: (_v: any, row: any) => (
          <div className="flex items-center justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => editBrandWeight(row)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ),
      });
    }
    return cols;
  }, [hasEditAccess]);

  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader
        title={`Goal Matrix ${!searchText.trim() ? `(${brandPubList.length})` : `(${brandPubList.length} of ${brandPubListBackup.length})`}`}
        renderCustomTop={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => throttleGetBrandGoals()}
              className="flex items-center gap-2 h-8 px-3"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            {admin_data && (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="flex items-center gap-2 h-8 px-3"
              >
                <a
                  rel="noreferrer"
                  href={`https://app.lift.lt.partners/testload?_id=${admin_data?.user}&token=${admin_data?.token}&onboard=${1}`}
                  target="_blank"
                >
                  Go to LIFT
                </a>
              </Button>
            )}
            <div className="relative ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search goal matrix..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  searchOptimized(e.target.value);
                }}
                className="pl-10 w-80"
              />
            </div>
          </div>
        }
      />

      <div className="flex-1 flex flex-col min-h-0 px-8 pt-8 pb-4">
        <div className="flex-1 min-h-0">
          <DataTable
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="calc(100vh - 170px)"
            columns={dataTableColumns}
            data={paginatedData}
            loading={listLoad}
            emptyMessage={searchText ? 'No brand goals found matching your search.' : 'No brand goals found.'}
            stickyHeader
            stickyFooter
            showActions={false}
            pagination={{
              currentPage: pageIndex + 1,
              totalPages: Math.max(1, Math.ceil(brandPubList.length / pageSize)),
              pageSize,
              totalItems: brandPubList.length,
              onPageChange: (p) => setPageIndex(p - 1),
              onPageSizeChange: (size) => { setPageSize(size); setPageIndex(0); },
              pageSizeOptions: [25, 50, 100, 200],
            }}
          />
        </div>
      </div>

      {/* Edit Brand Goal Sheet */}
      <Sheet
        open={showEditBrandWeightDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowEditBrandWeightDialog(false);
          }
        }}
      >
        <SheetContent
          side="right"
          className="w-[700px] sm:max-w-[700px] flex flex-col h-full overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>Edit Brand Goal Matrix</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-4 p-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="revenue">Revenue *</Label>
                  <Input
                    id="revenue"
                    type="number"
                    name="revenue"
                    value={brandGoals.revenue || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="spend">Spend *</Label>
                  <Input
                    id="spend"
                    type="number"
                    name="spend"
                    value={brandGoals.spend || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="comm_rate">Comm Rate *</Label>
                  <Input
                    id="comm_rate"
                    type="number"
                    name="comm_rate"
                    value={brandGoals.comm_rate || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="conv_rate">Conv Rate *</Label>
                  <Input
                    id="conv_rate"
                    type="number"
                    name="conv_rate"
                    value={brandGoals.conv_rate || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clicks">Clicks *</Label>
                  <Input
                    id="clicks"
                    type="number"
                    name="clicks"
                    value={brandGoals.clicks || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="actions">New Actions *</Label>
                  <Input
                    id="actions"
                    type="number"
                    name="actions"
                    value={brandGoals.actions || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cac">CAC *</Label>
                  <Input
                    id="cac"
                    type="number"
                    name="cac"
                    value={brandGoals.cac || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="canc">CAnC</Label>
                  <Input
                    id="canc"
                    type="number"
                    name="canc"
                    value={brandGoals.canc || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new_user">New Users % *</Label>
                  <Input
                    id="new_user"
                    type="number"
                    name="new_user"
                    value={brandGoals.new_user || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new_rev">New Rev % *</Label>
                  <Input
                    id="new_rev"
                    type="number"
                    name="new_rev"
                    value={brandGoals.new_rev || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="discount">Discount % *</Label>
                  <Input
                    id="discount"
                    type="number"
                    name="discount"
                    value={brandGoals.discount || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="aov">AOV *</Label>
                  <Input
                    id="aov"
                    type="number"
                    name="aov"
                    value={brandGoals.aov || ''}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="roas">ROAS *</Label>
                  <Input
                    id="roas"
                    type="number"
                    name="roas"
                    value={brandGoals.roas || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>
          <SheetFooter className="border-t pt-4 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowEditBrandWeightDialog(false)}
            >
              Cancel
            </Button>
            {hasEditAccess && (
              <Button
                onClick={updateBrandGoal}
                disabled={!hideEditBrandWeightProgressDialog}
              >
                {hideEditBrandWeightProgressDialog ? 'Save' : <SpinnerLoader size={16} />}
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
});

MatrixComponent.displayName = 'MatrixComponent';

export default MatrixComponent;
